/**
 * <mb-carousel>: carrossel Swiper carregado sob demanda (portado de pages/home/*.js e components/carousel.js).
 * - Inicializa quando entra na viewport (IntersectionObserver), carregando assets/swiper-bundle.min.{css,js} uma vez.
 * - Opções do Swiper vêm em data-options (JSON); paginação e setas são ligadas aos elementos internos.
 * - data-autoplay="7000": autoplay parado até a primeira interação (scroll/mousemove), como no tema anterior;
 *   respeita prefers-reduced-motion.
 * - Antes da inicialização, base.css mantém os slides em linha (sem salto de layout).
 */
const { loadScript, onFirstInteraction } = window.MB;

let cssPromise = null;

function loadStyle(href) {
  if (cssPromise) return cssPromise;
  cssPromise = new Promise((resolve) => {
    if (document.querySelector(`link[href="${href}"]`)) return resolve();
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.addEventListener('load', resolve);
    link.addEventListener('error', resolve);
    document.head.appendChild(link);
  });
  return cssPromise;
}

class MbCarousel extends HTMLElement {
  connectedCallback() {
    this.container = this.querySelector('.swiper');
    if (!this.container) return;
    const slides = this.container.querySelectorAll('.swiper-slide').length;
    if (slides < 2) {
      this.classList.add('-static');
      return;
    }
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            this.init();
          }
        },
        { rootMargin: '300px 0px' }
      );
      observer.observe(this);
    } else {
      this.init();
    }
  }

  async init() {
    if (this.swiper) return;
    const { swiperSrc, swiperCss } = this.dataset;
    await Promise.all([loadStyle(swiperCss), loadScript(swiperSrc)]);
    if (!window.Swiper || this.swiper) return;

    let options = {};
    try {
      options = JSON.parse(this.dataset.options || '{}');
    } catch (error) {
      options = {};
    }

    const pagination = this.container.querySelector('.swiper-pagination');
    const next = this.querySelector('.swiper-button-next');
    const prev = this.querySelector('.swiper-button-prev');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplayDelay = Number(this.dataset.autoplay || 0);

    const config = {
      speed: reduceMotion ? 0 : 1000,
      watchOverflow: true,
      ...options,
    };
    if (pagination) config.pagination = { el: pagination, type: 'bullets', clickable: true };
    if (next || prev) config.navigation = { nextEl: next, prevEl: prev };
    if (autoplayDelay && !reduceMotion) {
      config.autoplay = { delay: autoplayDelay, disableOnInteraction: false };
    }

    this.swiper = new window.Swiper(this.container, config);
    this.classList.add('-ready');

    if (config.autoplay) {
      this.swiper.autoplay.stop();
      onFirstInteraction(() => this.swiper && this.swiper.autoplay.start());
    }
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, false);
      this.swiper = null;
    }
  }
}

if (!customElements.get('mb-carousel')) customElements.define('mb-carousel', MbCarousel);
